import React, { useState } from 'react';
import { Modal, Form, Input, Switch, Button, message, Space, Row, Col, Divider } from 'antd';
import { PlusOutlined, TagsOutlined, FileTextOutlined, CloseOutlined, BgColorsOutlined } from '@ant-design/icons';
import { tagsApi } from '../../api/tags/tags.api';
import { CreateTagRequest } from '../../common/tag.interface';

interface CModalAddTagProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const CModalAddTag: React.FC<CModalAddTagProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      const tagData: CreateTagRequest = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        color: values.color || '#1890ff',
        isActive: values.isActive ?? true,
      };

      await tagsApi.createTag(tagData);
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.response?.status === 409) {
        message.error('Tag với tên này đã tồn tại');
      } else if (error.response?.status === 400) {
        message.error('Dữ liệu không hợp lệ');
      } else {
        message.error('Không thể tạo tag');
      }
      console.error('Error creating tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined />
          <span>Thêm Tag Mới</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      className="tags-modal"
      destroyOnClose
      maskClosable={true}
    >
      <div className="modal-content">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
            color: '#1890ff'
          }}
          className="tag-form"
        >
          <Row gutter={[16, 12]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <TagsOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Tên Tag</span>
                  </Space>
                }
                name="name"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên tag' },
                  { min: 2, message: 'Tên tag phải có ít nhất 2 ký tự' },
                  { max: 50, message: 'Tên tag không được vượt quá 50 ký tự' },
                ]}
              >
                <Input 
                  placeholder="Nhập tên tag"
                  className="form-input"
                  maxLength={50}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <BgColorsOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Màu sắc</span>
                  </Space>
                }
                name="color"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Input
                    type="color"
                    style={{ width: '50px', padding: '4px', border: '1px solid #d9d9d9', borderRadius: '6px' }}
                    className="form-input"
                  />
                  <Input
                    placeholder="#1890ff"
                    style={{ flex: 1 }}
                    maxLength={7}
                    className="form-input"
                  />
                </div>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space>
                    <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Mô tả</span>
                  </Space>
                }
                name="description"
                rules={[
                  { max: 200, message: 'Mô tả không được vượt quá 200 ký tự' },
                ]}
              >
                <Input.TextArea
                  placeholder="Nhập mô tả cho tag (tùy chọn)"
                  rows={3}
                  maxLength={200}
                  className="form-input"
                />
              </Form.Item>
            </Col>



            <Col span={24}>
              <Form.Item
                label="Trạng thái"
                name="isActive"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Hoạt động" 
                  unCheckedChildren="Vô hiệu hóa"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }} />

          <div className="modal-actions">
            <Button
              onClick={handleCancel}
              className="cancel-button"
              icon={<CloseOutlined />}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="save-button"
              icon={<PlusOutlined />}
            >
              Thêm Tag
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default CModalAddTag;
